/**
 * Bewertungskriterien für CherryPulse
 *
 * RESTRUKTURIERUNG (April 2026):
 * - Agency: technicalExpertise aufgelöst, guidelineAdherence entfernt
 * - Team: Flat auf eine Ebene, Produktivität konsolidiert
 * - Chemistry: trustFeeling + budgetAdherence entfernt, Verlässlichkeit + Termintreue zusammengelegt
 * - Sterne-System: 0-5 Sterne
 */

import type {
  EvaluationLevel,
  AgencyMainCriterionId,
  TeamMainCriterionId,
  ChemistryMainCriterionId,
  LonglistCriterionId,
  MainCriterionId,
} from '@/types/rating.types';
import { LEVEL_WEIGHTS } from '@/types/rating.types';

// ============================================================================
// CRITERION INTERFACES
// ============================================================================

/**
 * Basis-Interface für ein Bewertungskriterium
 */
export interface Criterion {
  id: string;
  label: string;
  description: string;
  icon: string;
  optional?: boolean;
}

/**
 * Hauptkriterium
 */
export interface MainCriterion<TMainId extends MainCriterionId> extends Criterion {
  id: TMainId;
}

/**
 * Konfiguration einer Bewertungsebene
 */
export interface EvaluationLevelConfig<TMainId extends MainCriterionId> {
  id: EvaluationLevel;
  label: string;
  shortLabel: string;
  description: string;
  icon: string;
  weight: number;
  mainCriteria: MainCriterion<TMainId>[];
}

// ============================================================================
// AGENCY LEVEL - Eignung & Kompetenzen (33%)
// ============================================================================

export const AGENCY_MAIN_CRITERIA: MainCriterion<AgencyMainCriterionId>[] = [
  {
    id: 'leadAgencySuitability',
    label: 'Agentur-Typ',
    description: 'Wie gut passt die Spezialisierung der Agentur (Lead, Spezial, Full-Service) zu den Anforderungen?',
    icon: '🏆',
  },
  {
    id: 'strategicCompetence',
    label: 'Strategische Kompetenz',
    description: 'Wie überzeugend war die strategische Herleitung und die Entwicklung tragfähiger Lösungsansätze?',
    icon: '🎯',
  },
  {
    id: 'workQuality',
    label: 'Qualität der Arbeitsergebnisse',
    description: 'Wie überzeugend war die handwerkliche Qualität und Fehlerfreiheit der gelieferten Ergebnisse?',
    icon: '✨',
  },
  {
    id: 'marketUnderstanding',
    label: 'Branchen- & Marktverständnis',
    description: 'Wie fundiert ist das Verständnis der Agentur für Branche, Markt und Zielgruppen des Kunden?',
    icon: '📊',
  },
  {
    id: 'internationalExperience',
    label: 'Internationale Erfahrung',
    description: 'Wie überzeugend ist die internationale Erfahrung und die Fähigkeit, über Märkte hinweg zu arbeiten?',
    icon: '🌍',
    optional: true,
  },
  {
    id: 'taskFit',
    label: 'Passung zur Aufgabenstellung',
    description: 'Wie gut passt die Agentur zur konkreten Aufgabenstellung und zum Kundenkontext?',
    icon: '🎯',
  },
];

export const AGENCY_LEVEL_CONFIG: EvaluationLevelConfig<AgencyMainCriterionId> = {
  id: 'agency',
  label: 'Eignung & Kompetenzen auf Agenturebene',
  shortLabel: 'Agentur',
  description: 'Bewertung der Agentur als Ganzes',
  icon: '🏢',
  weight: LEVEL_WEIGHTS.agency,
  mainCriteria: AGENCY_MAIN_CRITERIA,
};

// ============================================================================
// TEAM LEVEL - Fachliche Stärke des Kernteams (33%) - FLAT
// ============================================================================

export const TEAM_MAIN_CRITERIA: MainCriterion<TeamMainCriterionId>[] = [
  {
    id: 'strategyCompetence',
    label: 'Kompetenz Strategie',
    description: 'Wie überzeugend ist die strategische Kompetenz im Kernteam?',
    icon: '🎯',
  },
  {
    id: 'creativeCompetence',
    label: 'Kompetenz Kreation',
    description: 'Wie überzeugend ist die kreative Kompetenz im Kernteam?',
    icon: '🎨',
  },
  {
    id: 'targetedCreativity',
    label: 'Zielgerichtete Kreativität',
    description: 'Wie überzeugend waren die kreativen Ideen im Hinblick auf die Aufgabenstellung?',
    icon: '🎨',
  },
  {
    id: 'accountCompetence',
    label: 'Kompetenz Beratung/Account',
    description: 'Wie überzeugend ist die Beratungs- und Account-Kompetenz im Kernteam?',
    icon: '💼',
  },
  {
    id: 'consultingCompetence',
    label: 'Beratungskompetenz',
    description: 'Wie überzeugend waren die Empfehlungen und deren Begründungen?',
    icon: '💡',
  },
  {
    id: 'teamDynamics',
    label: 'Rollenverteilung & Teamzusammenspiel',
    description: 'Wie überzeugend ist die Rollenverteilung und das Zusammenspiel im Kernteam?',
    icon: '🔄',
  },
  {
    id: 'projectManagement',
    label: 'Projektmanagement',
    description: 'Wie überzeugend war die Projektsteuerung, Planung und Abstimmung?',
    icon: '📊',
  },
  {
    id: 'discussionQuality',
    label: 'Qualität der Diskussionen',
    description: 'Wie überzeugend war die Qualität der Diskussionen und Lösungsansätze?',
    icon: '💬',
  },
  {
    id: 'proactivity',
    label: 'Proaktivität',
    description: 'Wie proaktiv und impulsstark agierte das Team?',
    icon: '🚀',
  },
];

export const TEAM_LEVEL_CONFIG: EvaluationLevelConfig<TeamMainCriterionId> = {
  id: 'team',
  label: 'Fachliche Stärke des Kernteams',
  shortLabel: 'Team',
  description: 'Bewertung des zugeordneten Teams',
  icon: '👥',
  weight: LEVEL_WEIGHTS.team,
  mainCriteria: TEAM_MAIN_CRITERIA,
};

// ============================================================================
// CHEMISTRY LEVEL - Kulturelle Passung (33%)
// ============================================================================

export const CHEMISTRY_MAIN_CRITERIA: MainCriterion<ChemistryMainCriterionId>[] = [
  {
    id: 'teamFit',
    label: 'Teamfit & kulturelle Passung',
    description: 'Wie überzeugend ist die kulturelle Passung zwischen den Teams?',
    icon: '🤝',
  },
  {
    id: 'passionEnthusiasm',
    label: 'Herzblut & Begeisterung',
    description: 'Wie überzeugend waren Herzblut und Begeisterung für die Aufgabe?',
    icon: '❤️',
  },
  {
    id: 'communicationStyle',
    label: 'Kommunikationsstil',
    description: 'Wie überzeugend war der Kommunikationsstil im Austausch?',
    icon: '💬',
  },
  {
    id: 'chemistry',
    label: 'Chemie',
    description: 'Wie überzeugend war die persönliche Zusammenarbeit – möchten Sie weiter mit diesem Team arbeiten?',
    icon: '🤝',
  },
  {
    id: 'reliability',
    label: 'Verlässlichkeit & Termintreue',
    description: 'Wie verlässlich und termingerecht hat das Team gearbeitet?',
    icon: '✓',
  },
  {
    id: 'feedbackOpenness',
    label: 'Offenheit für Feedback',
    description: 'Wie offen und konstruktiv ging das Team mit Feedback um?',
    icon: '🔄',
  },
];

export const CHEMISTRY_LEVEL_CONFIG: EvaluationLevelConfig<ChemistryMainCriterionId> = {
  id: 'chemistry',
  label: 'Chemie',
  shortLabel: 'Chemie',
  description: 'Bewertung der zwischenmenschlichen Zusammenarbeit',
  icon: '🤝',
  weight: LEVEL_WEIGHTS.chemistry,
  mainCriteria: CHEMISTRY_MAIN_CRITERIA,
};

// ============================================================================
// LONGLIST LEVEL - Eigenes Schema für Longlist Workshop
// ============================================================================

export const LONGLIST_CRITERIA: MainCriterion<LonglistCriterionId>[] = [
  {
    id: 'longlistStrategy',
    label: 'Strategische Kompetenz',
    description: 'Fundierte strategische Herleitung (Markt, Zielgruppe, Wettbewerb), tragfähige Marken-/Kommunikationsplattformen, souveräner Umgang mit komplexen Aufgabenstellungen.',
    icon: '🎯',
  },
  {
    id: 'longlistCreativity',
    label: 'Kreative Exzellenz',
    description: 'Überzeugende, differenzierende Ideen mit hoher konzeptioneller Qualität, Relevanz und emotionaler Wirkung sowie exzellenter Umsetzung.',
    icon: '🎨',
  },
  {
    id: 'longlistDigital',
    label: 'Ganzheitliches & digitales Denken',
    description: 'Kanalübergreifendes Full-Thinking, ausgeprägt digitale Kompetenz sowie konsistente Umsetzung über alle relevanten Touchpoints.',
    icon: '💻',
  },
  {
    id: 'longlistMarket',
    label: 'Branchen- & Marktverständnis',
    description: 'Relevante Erfahrung im Marktumfeld, fundiertes Verständnis von Zielgruppen und Wettbewerbsdynamiken.',
    icon: '📊',
  },
  {
    id: 'longlistCaseFit',
    label: 'Referenzen & Case-Fit',
    description: 'Nachvollziehbare, wirkungsstarke Cases mit klarem strategischen und kulturellen Fit zur aktuellen Aufgabe.',
    icon: '📋',
  },
  {
    id: 'longlistTeam',
    label: 'Team & Organisation',
    description: 'Überzeugende Zusammensetzung des Kernteams, passende Seniorität und relevante Expertise sowie organisatorische Leistungsfähigkeit.',
    icon: '👥',
  },
  {
    id: 'longlistMotivation',
    label: 'Motivation & Gesamtüberzeugung',
    description: 'Erkennbares Interesse an der Aufgabe und des Kunden, kulturelle Passung und überzeugender Gesamteindruck.',
    icon: '❤️',
  },
];

export const LONGLIST_LEVEL_CONFIG: EvaluationLevelConfig<LonglistCriterionId> = {
  id: 'longlist',
  label: 'Longlist-Bewertung',
  shortLabel: 'Longlist',
  description: 'Bewertungskriterien für den Longlist Workshop',
  icon: '📝',
  weight: LEVEL_WEIGHTS.longlist,
  mainCriteria: LONGLIST_CRITERIA,
};

// ============================================================================
// ALL LEVELS COMBINED
// ============================================================================

/**
 * Alle Bewertungsebenen in der Reihenfolge für den Rating-Flow
 */
/**
 * Standard-Bewertungsebenen (für alle Anlässe außer Longlist)
 */
export const EVALUATION_LEVELS = [
  AGENCY_LEVEL_CONFIG,
  TEAM_LEVEL_CONFIG,
  CHEMISTRY_LEVEL_CONFIG,
] as const;

/**
 * Longlist-Bewertungsebenen (nur für Anlass 'longlist_workshop')
 */
export const LONGLIST_EVALUATION_LEVELS = [
  LONGLIST_LEVEL_CONFIG,
] as const;

/**
 * Gibt die passenden Bewertungsebenen für einen Anlass zurück
 */
export function getEvaluationLevelsForOccasion(occasion?: string): readonly (typeof EVALUATION_LEVELS[number] | typeof LONGLIST_LEVEL_CONFIG)[] {
  if (occasion === 'longlist_workshop') {
    return LONGLIST_EVALUATION_LEVELS;
  }
  return EVALUATION_LEVELS;
}

/**
 * Gibt die Konfiguration für eine Ebene zurück
 */
export function getLevelConfig(level: EvaluationLevel): (typeof EVALUATION_LEVELS)[number] | undefined {
  return EVALUATION_LEVELS.find((l) => l.id === level);
}

/**
 * Zählt alle Hauptkriterien über alle Ebenen
 */
export const TOTAL_MAIN_CRITERIA_COUNT =
  AGENCY_MAIN_CRITERIA.length + TEAM_MAIN_CRITERIA.length + CHEMISTRY_MAIN_CRITERIA.length;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Findet ein Hauptkriterium nach ID
 */
export function getMainCriterionById(id: MainCriterionId): Criterion | undefined {
  for (const level of EVALUATION_LEVELS) {
    const found = level.mainCriteria.find((c) => c.id === id);
    if (found) return found;
  }
  return undefined;
}

/**
 * Findet ein beliebiges Kriterium nach ID
 */
export function getCriterionById(id: string): Criterion | undefined {
  return getMainCriterionById(id as MainCriterionId);
}
